import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isLuxPlusSubscriptionItem,
  isTourV2ExperienceItem,
  isSubscriptionJoinItem,
  isTourV2Item, isInsuranceItem,
  findPostPurchaseCheckout,
  isBedbankItem,
  isLEHotelItem,
  isLoyaltyLoungePassItem,
  isLoyaltyTransferItem,
  isExperienceItem,
  isFlightItem,
} from 'lib/checkout/checkoutUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import isPaymentScheduleEnabled from './isPaymentScheduleEnabled'
import { getCartHasPropertyCollectItem } from '../propertyCollectSelectors'
import { isLpc50DepositExperimentControlSuppressesSchedules } from './lpc50PercentDepositPaymentSchedule'
import { isLpp50DepositExperimentControlSuppressesSchedules } from './lpp50PercentDepositPaymentSchedule'

const isPaymentScheduleSupportedLoyaltyItem = (item: App.Checkout.AnyItem): boolean => {
  return isLoyaltyTransferItem(item) || isLoyaltyLoungePassItem(item)
}

const isItemAllowedWithPaymentSchedules = (item: App.Checkout.AnyItem): boolean => {
  return isTourV2Item(item) ||
    isTourV2ExperienceItem(item) ||
    isLuxPlusSubscriptionItem(item) ||
    isSubscriptionJoinItem(item) ||
    isInsuranceItem(item) ||
    isBedbankItem(item) ||
    isLEHotelItem(item) ||
    isPaymentScheduleSupportedLoyaltyItem(item) ||
    isExperienceItem(item) ||
    isFlightItem(item)
}

const canGetItemsPaymentScheduleInfo = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.offer.bedbankOfferRates,
  (items, offers, isGift, bedbankRatesByOffer): Array<boolean> | false => {
    if (isGift || (!items.every(isItemAllowedWithPaymentSchedules))) {
      return false
    }

    const tourV2Items = items.filter(isTourV2Item)
    const bedbankItems = items.filter(isBedbankItem)
    const hotelItems = items.filter(isLEHotelItem)
    const flightItems = items.filter(isFlightItem)

    // Payment schedules for bedbank items are only supported when there is exactly one bedbank item and no tour items
    const hasOnlyOneBedbankItem = bedbankItems.length === 1
    const hasNoTourItems = tourV2Items.length === 0
    const hasNoHotelItems = hotelItems.length === 0
    if (hasNoTourItems && !hasOnlyOneBedbankItem && hasNoHotelItems) {
      return false
    }

    // For Flash hotel items (LE hotel items), allow multiple items from the same offer
    if (hotelItems.length > 1) {
      const uniqueOfferIds = new Set(hotelItems.map(item => item.offerId))
      if (uniqueOfferIds.size > 1) {
        return false
      }
    }

    return [...tourV2Items, ...bedbankItems, ...hotelItems, ...flightItems].map((item) => {
      if (isTourV2Item(item) || (isBedbankItem(item) && tourV2Items.length > 0)) {
        const tourItem = isTourV2Item(item) ? item : tourV2Items[0]
        const tourV2Offer = offers[getTourV2IdForCheckoutItem(tourItem)] as App.Tours.TourV2Offer | undefined
        const templateId = tourV2Offer?.paymentScheduleInfo?.staticSchedule?.templateId
        return !!templateId
      } else if (isBedbankItem(item)) {
        const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
        const bedbankOfferRates = bedbankRatesByOffer[item.offerId]?.[rateKey]
        const bedbankRate = bedbankOfferRates?.find(rate => rate.id === item.roomRateId)
        return !!bedbankRate?.paymentScheduleInfo?.staticSchedule?.templateId
      } else if (isLEHotelItem(item)) {
        const hotelOffer = offers[item.offerId] as App.HotelOffer | undefined
        // LPC items carry paymentScheduleInfo from the pricing API (svc-accommodation);
        // flash items fall back to the offer-level paymentScheduleInfo.
        if (item.paymentScheduleInfo) {
          return !!(item.paymentScheduleInfo.staticSchedule?.templateId || item.paymentScheduleInfo.dynamicSchedule?.templateId)
        }
        return !!(hotelOffer?.paymentScheduleInfo?.staticSchedule?.templateId || hotelOffer?.paymentScheduleInfo?.dynamicSchedule?.templateId)
      } else if (isFlightItem(item)) {
        // Flights without paymentScheduleSupported will be paid upfront (excluded from schedule)
        // but they don't block payment schedules for other items
        return true
      }
      return false
    })
  },
)

const isAnyItemDisabledForPaymentSchedule = createSelector(
  canGetItemsPaymentScheduleInfo,
  getCartHasPropertyCollectItem,
  (itemsPaymentScheduleInfo, hasPropertyCollectItem): boolean => {
    return !itemsPaymentScheduleInfo || itemsPaymentScheduleInfo.some(item => !item) || hasPropertyCollectItem
  },
)

const isPaymentScheduleAvailable = createSelector(
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isAnyItemDisabledForPaymentSchedule(state),
  (state: App.State) => isLpc50DepositExperimentControlSuppressesSchedules(state),
  (state: App.State) => isLpp50DepositExperimentControlSuppressesSchedules(state),
  (
    isPaymentScheduleEnabled,
    postPurchase,
    isAnyItemDisabledForPaymentSchedule,
    lpc50ControlSuppressSchedules,
    lpp50ControlSuppressSchedules,
  ): boolean => {
    if (lpc50ControlSuppressSchedules || lpp50ControlSuppressSchedules) {
      return false
    }
    return (
      isPaymentScheduleEnabled &&
      !postPurchase &&
      !isAnyItemDisabledForPaymentSchedule
    )
  },
)

export default isPaymentScheduleAvailable
